import { motion } from "framer-motion";
import { Mail, Phone, MessageSquareText, ArrowRight, Instagram, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/brand";
import { useT } from "../lib/i18n";

export default function Footer({ onOpenBookText }) {
  const { t } = useT();
  const seo = t("footer.seo");
  return (
    <footer
      id="contact"
      className="bg-black text-white pt-20 md:pt-24 pb-8 border-t border-white/10 relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 relative z-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <p className="overline text-[color:var(--primary)] mb-3">{t("footer.overline")}</p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-white text-5xl md:text-6xl lg:text-8xl leading-[0.9]"
            >
              {t("footer.heading1")} <span className="text-[color:var(--primary)]">{t("footer.heading2")}</span>
            </motion.h2>
            <p className="mt-5 text-white/60 text-sm md:text-base max-w-xl leading-relaxed">
              {t("footer.subhead")}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={onOpenBookText}
                className="inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3 transition-colors"
                data-testid="footer-book-text"
              >
                <MessageSquareText size={13} /> {t("footer.bookViaText")}
              </button>
              <Link
                to="/waiver"
                className="inline-flex items-center gap-2 border border-white/20 hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3 transition-colors"
                data-testid="footer-waiver-link"
              >
                {t("footer.signWaiver")} <ArrowRight size={13} />
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 grid grid-cols-2 gap-6 md:gap-10">
            <div>
              <p className="overline text-white/40 mb-3">{t("footer.contact")}</p>
              <ul className="space-y-2.5">
                <li>
                  <a href={`tel:${BRAND.phoneRaw}`} className="link-under inline-flex items-center gap-2 text-sm">
                    <Phone size={12} /> {BRAND.phone}
                  </a>
                </li>
                <li>
                  <a href={`sms:${BRAND.phoneRaw}`} className="link-under inline-flex items-center gap-2 text-sm">
                    <MessageSquareText size={12} /> {t("footer.clickToText")}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${BRAND.email}`} className="link-under inline-flex items-center gap-2 text-sm break-all">
                    <Mail size={12} /> {BRAND.email}
                  </a>
                </li>
              </ul>
              <div className="mt-5 flex items-center gap-2.5">
                <a href="#" className="p-1.5 border border-white/15 hover:bg-white hover:text-black transition-colors" aria-label="Instagram" data-testid="footer-instagram">
                  <Instagram size={14} />
                </a>
                <a href="#" className="p-1.5 border border-white/15 hover:bg-white hover:text-black transition-colors" aria-label="Facebook" data-testid="footer-facebook">
                  <Facebook size={14} />
                </a>
              </div>
            </div>
            <div>
              <p className="overline text-white/40 mb-3">{t("footer.explore")}</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#fleet" className="link-under">{t("footer.links.fleet")}</a></li>
                <li><a href="#tours" className="link-under">{t("footer.links.manifesto")}</a></li>
                <li><a href="#tour-info" className="link-under">{t("footer.links.beach")}</a></li>
                <li><a href="#locations" className="link-under">{t("footer.links.locations")}</a></li>
                <li><a href="#reviews" className="link-under">{t("footer.links.reviews")}</a></li>
                <li><Link to="/waiver" className="link-under">{t("footer.links.waiver")}</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* SEO copy block */}
        <div className="mt-16 border-t border-white/10 pt-8 grid md:grid-cols-3 gap-6 text-xs md:text-sm text-white/50 leading-relaxed">
          {seo.map((s, i) => (
            <p key={i}>
              <strong className="text-white">{s.title}</strong> — {s.body}
            </p>
          ))}
        </div>

        <div className="mt-8 pt-5 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[10px] uppercase tracking-widest font-mono text-white/40">
          <div className="flex items-center gap-2">
            <img src={BRAND.logo} alt="Rockstar ATVs" className="h-6 w-6 rounded-full bg-black" />
            <span>© {new Date().getFullYear()} Rockstar ATV Rentals · {t("footer.copyright")}</span>
          </div>
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
            <Link
              to="/privacy"
              className="hover:text-white transition-colors"
              data-testid="footer-privacy-link"
            >
              {t("footer.privacyPolicy")}
            </Link>
            <span className="text-white/20">·</span>
            <Link
              to="/privacy#messaging"
              className="hover:text-white transition-colors"
              data-testid="footer-messaging-link"
            >
              {t("footer.messagingTerms")}
            </Link>
            <span className="text-white/20 hidden md:inline">·</span>
            <span>www.RockStarATVRentals.com</span>
          </div>
        </div>

        <div className="mt-4 text-center text-[10px] uppercase tracking-widest font-mono text-white/40">
          {t("footer.createdBy")}{" "}
          <a
            href="https://www.GregMiceli.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[color:var(--primary)] hover:text-white transition-colors"
            data-testid="footer-credit-link"
          >
            www.GregMiceli.com
          </a>
        </div>
      </div>
    </footer>
  );
}
